import { DOCUMENT, DestroyRef, Injectable, Injector, NgZone, PLATFORM_ID, effect, inject, signal, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  AlertaApi, DatosDeAlerta, ItemDeInspiracion, NuevaAlertaDto, ParametrosDeEstimacion, RespuestaAlerta, RespuestaAlertas, RespuestaRecibidas,
} from 'src/app/interfaces/alert';
import { SessionService } from '../session/session.service';
import { INTERVALO_CAMPANA_MS } from '../notificaciones-campana/notificaciones-campana.service';

/** Donde se ve la barra inferior, la única que suma las recibidas sin leer a su número. */
const CONSULTA_MOVIL = '(max-width: 992px)';

/** Respuesta genérica de la API: `ok` y, si no, `reason`. */
interface RespuestaOk {
  ok: boolean;
  reason?: string;
}

/**
 * La única puerta de la web a `/alerts` (gestión avanzada de alertas, S14).
 *
 * Va directo a los métodos genéricos de michollo-client (`session.mc.get/put/patch/post/
 * delete`), que ya ponen el JWT y reintentan tras refrescarlo; no pasa por `RestService`.
 * Los ids de alerta son cadenas OPACAS (`t-12`, `k-34`): aquí se concatenan a la ruta y
 * nada más. Es seguro en SSR: sólo el recuento en vivo toca `window` y `document`, y sólo en el
 * navegador; nunca `localStorage`. El cliente lo construye `SessionService` con sus propias guardas.
 */
@Injectable({ providedIn: 'root' })
export class AlertasService {
  private session = inject(SessionService);

  private injector = inject(Injector);
  private zone = inject(NgZone);
  private document = inject<Document>(DOCUMENT);
  private esNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Alertas recibidas sin leer (`unread` de `GET /alerts/received`). Lo comparten la pestaña
   * Alertas de la barra inferior, que lo suma a los avisos de la campana, y la sub-pestaña
   * «Recibidas», que lo baja al marcar (odd/tasks/avisos-movil.md, S3, S5). Es de la cuenta
   * que hay: a 0 al salir o al cambiar de cuenta (S9).
   */
  readonly recibidasSinLeer = signal(0);

  private vigilando = false;
  /** Id de la cuenta a la que pertenece el número; `undefined` antes de mirar la sesión. */
  private cuenta: unknown = undefined;
  /** Sube con cada cambio de cuenta: una respuesta pedida antes ya no es de la cuenta actual. */
  private generacion = 0;
  private conSesion = false;
  private temporizador: ReturnType<typeof setInterval> | null = null;
  private escuchandoVisibilidad = false;

  /**
   * Recuento en vivo para la barra móvil (S8, S9), con la cadencia y las condiciones de la
   * campana (NotificacionesCampanaService): al entrar, cada 90 s con la pestaña visible y al
   * volver a ella; sólo en el navegador, con una cuenta verificada (las sin verificar reciben
   * 403 y el modal de «valida tu email») y donde se ve la barra (< 993 px). Al salir o cambiar
   * de cuenta, a 0 en todas partes. Lo arranca la barra inferior una vez; repetir no hace nada.
   */
  vigilarRecibidasSinLeer(): void {
    if (!this.esNavegador || this.vigilando) return;
    this.vigilando = true;
    this.injector.get(DestroyRef).onDestroy(() => this.parar());
    effect(() => {
      const usuario = this.session.user();
      untracked(() => this.alCambiarUsuario(usuario));
    }, { injector: this.injector });
  }

  private alCambiarUsuario(usuario: { id?: unknown; role?: string } | null) {
    const id = usuario?.id ?? null;
    const cambio = id !== this.cuenta;
    if (cambio) {
      this.cuenta = id;
      this.generacion++;
      this.recibidasSinLeer.set(0);
    }
    const verificada = !!usuario && usuario.role !== 'unverified';
    if (!verificada || !this.esMovil()) {
      this.parar();
      return;
    }
    // Editar el perfil vuelve a publicar el mismo usuario: sin cambio de cuenta, no se pide otra vez.
    if (!cambio && this.conSesion) return;
    this.conSesion = true;
    if (!this.escuchandoVisibilidad) {
      this.document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
      this.escuchandoVisibilidad = true;
    }
    void this.contarRecibidasSinLeer();
    if (!this.document.hidden) this.programar();
  }

  private esMovil(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(CONSULTA_MOVIL).matches;
  }

  private parar() {
    this.conSesion = false;
    this.desprogramar();
    if (this.escuchandoVisibilidad) {
      this.document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
      this.escuchandoVisibilidad = false;
    }
  }

  private alCambiarVisibilidad = () => {
    if (!this.conSesion) return;
    if (this.document.hidden) {
      this.desprogramar();
    } else {
      void this.contarRecibidasSinLeer();
      this.programar();
    }
  };

  // Fuera de la zona, como la campana: un intervalo vivo dentro no dejaría que la app se
  // estabilizara (el service worker se registra con `registerWhenStable`).
  private programar() {
    if (this.temporizador) return;
    this.zone.runOutsideAngular(() => {
      this.temporizador = setInterval(() => { void this.contarRecibidasSinLeer(); }, INTERVALO_CAMPANA_MS);
    });
  }

  private desprogramar() {
    if (!this.temporizador) return;
    clearInterval(this.temporizador);
    this.temporizador = null;
  }

  /**
   * Pide una sola fila de recibidas y se queda con `unread`; si falla, deja el que había. Si
   * entretanto ha salido o cambiado la cuenta, la respuesta se tira: no es de la cuenta actual.
   */
  async contarRecibidasSinLeer(): Promise<void> {
    const generacion = this.generacion;
    const resp = await this.recibidas(1, 0);
    if (generacion !== this.generacion) return;
    if (resp?.ok && typeof resp.unread === 'number') {
      const n = Math.max(0, resp.unread);
      this.zone.run(() => this.recibidasSinLeer.set(n));
    }
  }

  /** `GET /alerts`: la lista entera, la comunidad y los canales del usuario. */
  listar(): Promise<RespuestaAlertas> {
    return this.pedir(() => this.session.mc.get('alerts', undefined));
  }

  /** `PUT /alerts`: crea (o reactiva) una alerta de etiqueta o de palabra. */
  crear(dto: NuevaAlertaDto): Promise<RespuestaAlerta> {
    return this.pedir(() => this.session.mc.put('alerts', dto));
  }

  /** `PATCH /alerts/:id`: cambia sólo los campos que vienen. `kmh: -1` apaga la campana de una etiqueta. */
  editar(id: string, dto: Partial<DatosDeAlerta>): Promise<RespuestaAlerta> {
    return this.pedir(() => this.session.mc.patch(`alerts/${id}`, dto));
  }

  /** `DELETE /alerts/:id`: una palabra se borra; una etiqueta queda seguida con la campana apagada. */
  quitar(id: string): Promise<RespuestaOk> {
    return this.pedir(() => this.session.mc.delete(`alerts/${id}`));
  }

  /**
   * `DELETE /tags/:id/subscription`: deja de seguir la etiqueta del todo. Es lo que hace
   * desaparecer su alerta de la lista; `quitar` sólo le apaga la campana y la API la
   * seguiría devolviendo como «Sin avisos».
   */
  dejarDeSeguirEtiqueta(tagId: number): Promise<RespuestaOk> {
    return this.pedir(() => this.session.mc.delete(`tags/${tagId}/subscription`));
  }

  /** `PUT /alerts/community`: enciende o apaga «Chollos supersónicos». */
  comunidad(supersonic: boolean): Promise<RespuestaOk & { community?: { supersonic: boolean } }> {
    return this.pedir(() => this.session.mc.put('alerts/community', { supersonic }));
  }

  /** `GET /alerts/received`: los chollos que dispararon alguna alerta, paginados. */
  recibidas(limit = 20, offset = 0): Promise<RespuestaRecibidas> {
    return this.pedir(() => this.session.mc.get('alerts/received', { limit, offset }));
  }

  /** `POST /alerts/received/read`: marca leídas unas filas o todas. */
  marcarLeidas(ids: number[] | 'all'): Promise<RespuestaOk & { unread?: number }> {
    const cuerpo = ids === 'all' ? { all: true } : { ids };
    return this.pedir(() => this.session.mc.post('alerts/received/read', cuerpo));
  }

  /** `GET /alerts/inspiration`: etiquetas y palabras con más alertas creadas, sin las del usuario. */
  inspiracion(): Promise<RespuestaOk & { items?: ItemDeInspiracion[] }> {
    return this.pedir(() => this.session.mc.get('alerts/inspiration', undefined));
  }

  /**
   * `GET /alerts/estimate`: cuántos chollos por semana habrían coincidido. Los precios sin
   * filtro viajan como cadena vacía, que es como un formulario los pondría en la query.
   */
  estimar(params: ParametrosDeEstimacion): Promise<RespuestaOk & { per_week?: number }> {
    const query = { ...params, price_min: params.price_min ?? '', price_max: params.price_max ?? '' };
    return this.pedir(() => this.session.mc.get('alerts/estimate', query));
  }

  /**
   * michollo-client devuelve `error.response.data` cuando la API contesta mal y `undefined`
   * cuando ni contesta (red caída): aquí eso pasa a ser `{ ok: false }` para que quien
   * llama sólo tenga que mirar `ok`.
   */
  private async pedir<T extends RespuestaOk>(llamada: () => Promise<T>): Promise<T> {
    try {
      const resp = await llamada();
      return (resp ?? { ok: false, reason: 'NETWORK' }) as T;
    } catch (error) {
      console.error('[alertas]', error);
      return { ok: false, reason: 'NETWORK' } as T;
    }
  }
}

